import type { ReactNode } from "react";
import { useRbac } from "@/context/RbacContext";
import type { Permission } from "@/api/types";

export interface PermissionGateProps {
  /** Permission(s) required. With `mode="all"` ALL are required; default `mode="any"`. */
  permission: Permission | Permission[];
  mode?: "any" | "all";
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Renders children only when the user holds the required permission(s). Used to gate nav items,
 * dashboards/worklists (worklist:read), and management screens — keeping the UI honest with what the
 * server will actually allow. The server enforces the gate too; this is for a clean UX, not security.
 */
export function PermissionGate({
  permission,
  mode = "any",
  children,
  fallback = null,
}: PermissionGateProps) {
  const { hasPermission } = useRbac();
  const perms = Array.isArray(permission) ? permission : [permission];
  const allowed =
    mode === "all" ? perms.every((p) => hasPermission(p)) : perms.some((p) => hasPermission(p));
  return <>{allowed ? children : fallback}</>;
}
